// =============================================================================
// REACTION PICKER COMPONENT (Shared foundation #6)
// =============================================================================
//
// The curated six stay as the fast default row; a "+" expands a larger common
// set in the same popover. Targets are 44px (the app's tap-target standard,
// clearing the DSGN-3 regression) and 56px when the user's comfort mode is on.
// Built once here; each surface (chat / gallery / calls) owns its own sender.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore.js';
import { Plus } from '../ui/Icons.js';

const COMMON_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

// Extended set revealed by the "+". Kept to family-friendly, broadly useful
// emoji rather than a full unicode picker - enough range without overwhelm.
const EXTENDED_REACTIONS = [
  '👍', '👎', '❤️', '🔥', '🥰', '😍', '😂', '🤣',
  '😊', '😎', '😮', '😢', '😡', '🥳', '🎉', '🙏',
  '👏', '💪', '✨', '⭐', '💯', '✅', '👀', '🤔',
  '😴', '🤗', '😅', '🫶', '☘️', '🌟', '🎂', '🍀',
];

interface ReactionPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

export function ReactionPicker({ onSelect, onClose }: ReactionPickerProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Match the platform comfort-mode predicate (App.tsx / MemorySessionView):
  // both 'simplified' and 'accessibility' are Comfort Mode. The picker shipped
  // checking only 'accessibility', so simplified-mode users missed the 56px
  // targets the rest of the app gave them.
  const comfort = useAuthStore((s) => s.user?.uiMode === 'simplified' || s.user?.uiMode === 'accessibility');

  // 44px standard target, 56px under comfort mode.
  const targetSize = comfort ? 'h-14 w-14 text-3xl' : 'h-11 w-11 text-2xl';

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [onClose]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => { document.removeEventListener('keydown', handleEscape); };
  }, [onClose]);

  if (expanded) {
    return (
      <div
        ref={ref}
        className="grid grid-cols-8 gap-1 p-2 rounded-2xl bg-surface-3 shadow-xl border border-line-2 max-w-[22rem]"
      >
        {EXTENDED_REACTIONS.map((emoji, i) => (
          <button
            key={`${emoji}-${i}`}
            type="button"
            onClick={() => { onSelect(emoji); }}
            className={`${targetSize} flex items-center justify-center rounded-full hover:bg-surface-sunken transition-colors`}
            title={emoji}
          >
            {emoji}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="flex gap-1 p-2 rounded-full bg-surface-3 shadow-xl border border-line-2"
    >
      {COMMON_REACTIONS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => { onSelect(emoji); }}
          className={`${targetSize} flex items-center justify-center rounded-full hover:scale-110 hover:bg-surface-sunken transition-transform`}
          title={emoji}
        >
          {emoji}
        </button>
      ))}
      <button
        type="button"
        onClick={() => { setExpanded(true); }}
        aria-label={t('chat.moreReactions', 'More reactions')}
        className={`${targetSize} flex items-center justify-center rounded-full text-ink-2 hover:bg-surface-sunken transition-colors`}
      >
        <Plus className="w-5 h-5" />
      </button>
    </div>
  );
}
